import { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Edit, TrashAlt } from '@styled-icons/boxicons-solid'
import { Menu } from '@styled-icons/boxicons-regular/Menu'

import { useNavigate, useSearchParams } from 'react-router-dom'
import { deleteRequest, getRequest } from '../../services/base'
import { NavContext } from '../../context/global_context'
import {
  PROMOTE_CHANGES,
  TRACER_PREVIEW,
  TRACER_PREVIEW_MGMT,
} from '../../constants/permission'
import { HasPermission } from '../../utils/permission'
import { Col, Row, Table, Form } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import InputText from '../../antd/common/InputText'
import Button from '../../antd/common/Button'
import LinkWithStatus from '../../antd/common/LinkWithStatus'
import { ProgresSpinner } from '../../antd/common/ProgresSpinner'
import EllipsisTooltip from '../../antd/common/EllipsisTooltip'
import { useVehicles } from '../../context/vehicle_context'
import NoRecordsFound from '../../antd/common/NoRecordsFound'

dayjs.extend(customParseFormat)

const Action = (props) => {
  const navigate = useNavigate()
  const { setSelectedVehicle } = useVehicles()
  const vehicle = props.vehicle
  const blockers = (vehicle.blockers.slice() || []).filter(
    (b) => b.name !== 'graph_reset'
  )
  const vehicleIsBlocked = blockers.length > 0
  const messages = {
    wire_list: 'Importing Wirelist',
    dtcs: 'Importing DTCs',
    modules: 'Importing Modules',
    inlines: 'Importing Inlines',
    category_connector: 'Importing Connector Categories',
    harness_description: 'Importing Harness Descriptions',
    sig_list: 'Importing Circuit List',
    device_connections: 'Importing Device Connections',
    generating_wires: 'Wire Generation is in progress',
  }
  const state = blockers.map((b) => {
    return messages[b.name]
  }).join('. ')

  return (
    <>
      {vehicleIsBlocked && (
        <ProgresSpinner caption={state} className="blockers" size="small" />
      )}
      {!vehicleIsBlocked && (
        <LinkWithStatus
          onClickFunc={() => {
            setSelectedVehicle(vehicle)
            navigate(`/vehicles/${vehicle.vehicle_id}/importers`)
          }}
          className="ant-link"
          title="Browse"
        />
      )}
    </>
  )
}

const VehiclesTable = (props) => {
  const { getPaginatedData } = props

  const [form] = Form.useForm()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchQuery = searchParams.get('search') || ''

  const [state, dispatch] = useContext(NavContext)
  const { vehicles, setVehiclesData, pagination, setPagination } = useVehicles()
  const { currentPage, totalCount, totalPages, nextPageLink, prevPageLink } =
    pagination
  const navigate = useNavigate()

  useEffect(() => {
    form.setFieldsValue({ search: searchQuery })
  }, [searchQuery, form])
  useEffect(() => {
    fetchVehicles()
  }, [searchQuery])
  const onFinish = (values) => {
    setSearchParams(values.search ? { search: values.search } : {})
  }

  const fetchVehicles = async () => {
    try {
      const response = await getRequest(`/api/vehicles/?search=${searchQuery}`)
      setVehiclesData(response.results)
      setPagination(response.pagination)
    } catch (err) {
      console.error(err)
    }
  }
  /* Vehicle List Table Data */
  const data = vehicles?.map((vehicle, index) => {
    // vehicle.blockers.push({name: 'dtcs'})
    return {
      key: index,
      vehicleName: vehicle.name,
      makeModelId: vehicle.make_model_id,
      lastImportedAt: vehicle.last_imported_at,
      action: <Action vehicle={vehicle} />,
    }
  })
  /* Vehicle List Table columns */
  const columns = [
    {
      title: 'Vehicle Name',
      className: 'vehicle-name',
      dataIndex: 'vehicleName',
      sorter: (a, b) => a.vehicleName.length - b.vehicleName.length,
      ellipsis: { showTitle: false },
      align: 'left',
      width: '25%',
      render: (text) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Model ID',
      className: 'make-model-id',
      dataIndex: 'makeModelId',
      sorter: (a, b) => a.makeModelId - b.makeModelId,
      ellipsis: { showTitle: false },
      align: 'left',
      width: '18%',
      render: (text) => <EllipsisTooltip text={text} />,
    },
    {
      title: 'Last Import',
      className: 'last-imported-at',
      dataIndex: 'lastImportedAt',
      sorter: (a, b) =>
        dayjs(a.lastImportedAt, 'DD-MM-YYYY hh:mm A').valueOf() -
        dayjs(b.lastImportedAt, 'DD-MM-YYYY hh:mm A').valueOf(),
      defaultSortOrder: 'descend',
      ellipsis: { showTitle: false },
      align: 'left',
      width: '25%',
      render: (text) => <EllipsisTooltip text={text} />,
    },
    {
      title: '',
      className: 'action',
      dataIndex: 'action',
      ellipsis: { showTitle: false },
      align: 'right',
      // render: (text) => <EllipsisTooltip text={text} />,
    },
  ]

  return (
    <div className="page vehicle-list">
      <Row className="header">
        {/* Search Column */}
        <Col className="left-col">
          <Form form={form} layout="inline" onFinish={onFinish}>
            <InputText
              type="search"
              name="search"
              placeholder={`Search Make Model Id, Make, Model...`}
              initialValue={searchQuery}
              isRequired={false}
              onSubmit={(e) => {
                form.submit()
              }}
              wrapperClass="search-wrapper"
            />
          </Form>
        </Col>
        {/* Rest of the buttons */}
        <Col className="right-col">
          {HasPermission(PROMOTE_CHANGES) && (
            <LinkWithStatus
              href={`/manage-update-list`}
              title="Manage your Updates list"
            />
          )}
          {(state?.permissions?.includes(TRACER_PREVIEW) ||
            state?.permissions?.includes(TRACER_PREVIEW_MGMT)) && (
            <Button
              onClick={() => {
                navigate('/manage-update-list/preview?data_version=master')
              }}
            >
              Launch Tracer Preview
            </Button>
          )}
          <LinkWithStatus
            href={`/vehicles/new`}
            title="Create New Vehicle"
            type="primary"
          />
        </Col>
      </Row>
      <Row className="body">
        {vehicles?.length < 1 ? (
          <NoRecordsFound loading={false} />
        ) : (
          <>
            <p className="table-title">{`${totalCount} Vehicles`}</p>
            <Row className="table-container">
              <Table
                dataSource={data}
                columns={columns}
                loading={!vehicles ? true : false}
                // TODO: Check pagination by populating data
                pagination={
                  totalPages > 1
                    ? {
                        current: currentPage,
                        total: totalCount,
                        pageSize: 10,
                        onChange: (page) => {
                          if (page > currentPage && nextPageLink) {
                            getPaginatedData(nextPageLink)
                          }

                          if (page < currentPage && prevPageLink) {
                            getPaginatedData(prevPageLink)
                          }
                        },
                      }
                    : false
                }
              />
            </Row>
          </>
        )}
      </Row>
    </div>
  )
}

VehiclesTable.propTypes = {
  activityLogEnabled: PropTypes.bool,
  searchQuery: PropTypes.string,
  pagination: PropTypes.object,
  getPaginatedData: PropTypes.func,
}

export default VehiclesTable
